import {
  type CreatePatientInput,
  type PatientDetail,
  type Pricing,
  calculatePricing,
} from '#shared/types';
import type { SetField } from '#client/hooks/useFormValues';

/** Sets one field of the form. */
export type SetPatientValue = SetField<CreatePatientInput>;

/** What the counter starts a new visit from. */
function newPatientValues(): CreatePatientInput {
  return {
    name: '',
    gender: 'male',
    age_year: 0,
    age_month: 0,
    age_days: 0,
    refer_by: '',
    phone_number: '',
    cnic_number: '',
    email: '',
    note: '',
    discount_rupees: 0,
    discount_percentage: 0,
    tax_percentage: 0,
    other_charges: 0,
    other_charges_label: '',
    payment_amount: 0,
    payment_method: null,
    whatsapp_consent: false,
    married_status: 'single',
    panel_ids: [],
    test_ids: [],
  };
}

/** The visit being edited, or the defaults the counter starts a new one from. */
export function initialPatientValues(record: PatientDetail | undefined): CreatePatientInput {
  if (!record) return newPatientValues();
  return {
    name: record.name,
    gender: record.gender,
    age_year: record.age_year,
    age_month: record.age_month,
    age_days: record.age_days,
    refer_by: record.refer_by ?? '',
    phone_number: record.phone_number ?? '',
    cnic_number: record.cnic_number ?? '',
    email: record.email ?? '',
    note: record.note ?? '',
    discount_rupees: record.discount_rupees,
    discount_percentage: record.discount_percentage,
    tax_percentage: record.tax_percentage,
    other_charges: record.other_charges,
    other_charges_label: record.other_charges_label ?? '',
    // What changes hands with *this* save. Money already taken is in the ledger
    // and is never re-posted, so editing a visit cannot erase it.
    payment_amount: 0,
    payment_method: null,
    whatsapp_consent: record.whatsapp_consent,
    married_status: record.married_status,
    panel_ids: record.panels.map((panel) => panel.id),
    test_ids: record.reports.map((report) => report.test_id),
  };
}

/**
 * The bill as the form currently describes it.
 *
 * Only a preview: the server runs this same function over `LabTest.price` and
 * stores what it computes. Sending these figures was how a crafted request
 * could book any total.
 */
export function previewPatientPricing(
  values: CreatePatientInput,
  alreadyPaid: number,
  priceById: ReadonlyMap<number, number>,
): Pricing {
  return calculatePricing(
    { ...values, paid: alreadyPaid + values.payment_amount },
    values.test_ids.map((testId) => priceById.get(testId) ?? 0),
  );
}
